/**
 * Badge — small inline label (Claude Design `.badge`): a 21px-tall pill,
 * font 11.5/700, with semantic tones. `square` swaps the full-radius pill for a
 * rounded-rect chip.
 *
 * `StatusPill` is the 23px status pill (`.pill`) that prefixes the label with a
 * leading colored dot, mapping a known status string to a semantic tone.
 */
import type { HTMLAttributes, ReactElement } from 'react';

import { cn } from './cn.js';
import { Pill, type PillTone } from './Pill.js';

export type BadgeTone =
  | 'neutral'
  | 'primary'
  | 'ok'
  | 'warn'
  | 'danger'
  | 'info'
  | 'outline';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  /** Rounded-rect chip instead of the default full-radius pill. */
  square?: boolean;
}

const toneClasses: Record<BadgeTone, string> = {
  neutral: 'bg-surface-3 text-muted',
  primary: 'bg-primary-soft text-primary',
  ok: 'bg-ok-soft text-ok',
  warn: 'bg-warn-soft text-warn',
  danger: 'bg-danger-soft text-danger',
  info: 'bg-info-soft text-info',
  outline: 'border border-border-strong text-muted',
};

export function Badge({
  tone = 'neutral',
  square = false,
  className,
  ...props
}: BadgeProps): ReactElement {
  return (
    <span
      data-tone={tone}
      className={cn(
        'inline-flex h-[21px] items-center gap-[5px] whitespace-nowrap px-2 ' +
          'text-[11.5px] font-bold leading-none tracking-[0.01em] [&_svg]:size-3',
        square ? 'rounded-sm' : 'rounded-full',
        toneClasses[tone],
        className,
      )}
      {...props}
    />
  );
}

/**
 * StatusPill — maps a known status string to a semantic tone and renders it as
 * the design's `.pill` (23px, leading colored dot). Falls back to the `neutral`
 * tone for unrecognized values, and renders the label with underscores turned
 * into spaces (matching the legacy StatusBadge behavior).
 */
export interface StatusPillProps extends Omit<BadgeProps, 'tone' | 'children' | 'square'> {
  status: string;
}

const STATUS_TONE: Record<string, BadgeTone> = {
  // positive / terminal-good
  active: 'ok',
  sent: 'ok',
  resolved: 'ok',
  connected: 'ok',
  // in-progress / attention
  open: 'info',
  queued: 'warn',
  triaged: 'warn',
  triaging: 'warn',
  pending: 'warn',
  // negative
  suspended: 'danger',
  failed: 'danger',
  spam: 'danger',
  error: 'danger',
  // inactive / terminal-neutral (explicit muted tone rather than the fallback)
  closed: 'neutral',
  disabled: 'neutral',
  inactive: 'neutral',
};

export function statusTone(status: string): BadgeTone {
  return STATUS_TONE[status.toLowerCase()] ?? 'neutral';
}

export function StatusPill({
  status,
  className,
  ...props
}: StatusPillProps): ReactElement {
  const tone: PillTone = statusTone(status);
  return (
    <Pill tone={tone} data-status={status} className={className} {...props}>
      {status.replace(/_/g, ' ')}
    </Pill>
  );
}
